import { useTranslations } from 'next-intl';
import { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { IntegrationEnum } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { usePathname, useSearchParams } from 'next/navigation';
import { useUniversalChatThreads } from 'providers/ChatProviders/useUniversalChatThreads';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { KeysEnum } from 'types/KeysEnum';
import { useDebouncedCallback } from 'use-debounce';
import { markdownToFile } from 'utils/markdownToFile';
import { extractParam } from 'utils/paramsUtil';
import { setFileToUpload, updateInputField, useUploadingContentFlow } from '../_actions';

interface IChatAiIntegration {
  setFileToUpload: (file: File) => void;
  useUploadingContentFlow: () => void;
  updateInputField: (data: { field: string; value: string }) => void;
  allowedTypes: string[];
  supportedFormats: string[];
}

const ChatAiIntegration: FC<IChatAiIntegration> = ({
  setFileToUpload,
  useUploadingContentFlow: uploadingContentFlow,
  updateInputField,
  allowedTypes
}) => {
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [loading, setLoading] = useState(false);

  const id = extractParam(searchParams, KeysEnum.Id, null) as string;
  const index = parseInt(extractParam(searchParams, KeysEnum.Index, null), 10) as number;
  const addon = pathname.split('/translation/new/')?.at(1);

  const { data: threadPage } = useUniversalChatThreads(
    { id: id },
    { enabled: !!id && addon === IntegrationEnum.ChatAi }
  );

  const fetchFiles = useCallback(async () => {
    if (addon === IntegrationEnum.ChatAi && !!id && !loading && !!threadPage) {
      try {
        setLoading(true);
        setFileToUpload(
          markdownToFile(
            threadPage?.nodes?.at(0)?.messages?.nodes?.at(index)?.content ?? '',
            'Tolq Ai - Text'
          )
        );
        updateInputField({ field: 'integration', value: IntegrationEnum.ChatAi });
        uploadingContentFlow();
      } catch (error) {
        notifyException(error, ESpansException.GetIntegration);
        toaster.error(t('messages.error.something_happen'));
      }
    }
  }, [
    addon,
    id,
    loading,
    threadPage,
    setFileToUpload,
    index,
    updateInputField,
    uploadingContentFlow,
    t
  ]);

  const debouncedMutation = useDebouncedCallback(fetchFiles, 1);

  useEffect(() => {
    debouncedMutation();
  }, [id, index, threadPage, addon, loading, fetchFiles]);

  return null;
};

export default connect(
  ({ staticData: { supportedFormats, allowedTypes } }) => ({ supportedFormats, allowedTypes }),
  {
    setFileToUpload,
    useUploadingContentFlow,
    updateInputField
  }
)(ChatAiIntegration);
